import React, { useState } from 'react';
import AddChildModal from './AddChildModal';

const ReassuringChildPrompt = ({ isOpen, onClose, onChildCreated, api, onUpgrade }) => {
  const [showAddChild, setShowAddChild] = useState(false);
  const [showTestOptions, setShowTestOptions] = useState(false);
  const [testAge, setTestAge] = useState(6);
  const [testGender, setTestGender] = useState('boy');
  const [isCreatingTest, setIsCreatingTest] = useState(false);

  const handleCreateTestChild = async () => {
    setIsCreatingTest(true);
    try {
      const response = await api.post('/children/test', {
        age: testAge,
        gender: testGender
      });

      if (response.data && response.data.child) {
        onChildCreated(response.data.child);
        onClose();
      } else {
        throw new Error('Réponse invalide');
      }
    } catch (error) {
      console.error('Erreur création enfant test:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Erreur lors de la création';
      alert(`Oups ! ${errorMessage}`);
    } finally {
      setIsCreatingTest(false);
    }
  };

  const handleRealChildCreated = async (childData) => {
    try {
      const response = await api.post('/children', {
        name: childData.name,
        age: childData.age,
        birth_month: childData.birth_month,
        birth_year: childData.birth_year,
        gender: childData.gender
      });

      if (response.status === 200 || response.status === 201) {
        const createdChild = response.data.child || response.data;
        setTimeout(() => {
          onChildCreated(createdChild);
          onClose();
        }, 100);
        return createdChild;
      } else {
        throw new Error(`Réponse inattendue: ${response.status}`);
      }
    } catch (error) {
      console.error('Erreur création enfant:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Erreur lors de l\'ajout';
      throw new Error(errorMessage);
    }
  };

  if (!isOpen) return null;

  if (showAddChild) {
    return (
      <AddChildModal
        isOpen={true}
        onClose={() => setShowAddChild(false)}
        onAddChild={handleRealChildCreated}
        onUpgrade={onUpgrade}
        isFirstChild={true}
      />
    );
  }

  return (
    <>
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@700&display=swap');`}
      </style>
      
      {/* Overlay avec fond dégradé */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Background avec le gradient de l'app */}
        <div 
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}
        />
        
        {/* Contenu */}
        <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto pb-24">
          
          {/* Header avec mascotte */}
          <div className="text-center mb-6">
            <div className="relative inline-block">
              <div className="text-7xl mb-2 animate-bounce">🎈</div>
              <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-16 h-2 bg-black/10 rounded-full blur-sm"></div>
            </div>
            <h1 
              className="text-3xl font-bold text-gray-800 mb-2"
              style={{ fontFamily: '"Caveat", cursive', textShadow: '0 2px 4px rgba(255,255,255,0.5)' }}
            >
              Créons ensemble !
            </h1>
            <p className="text-teal-700 font-medium">
              Prêt pour l'aventure ? ✨
            </p>
          </div>
          
          {/* Carte explicative */}
          <div className="bg-white/70 backdrop-blur-md rounded-3xl p-6 shadow-xl border border-white/50 mb-4">
            
            {/* Pourquoi */}
            <div className="mb-5">
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <span className="text-xl">🎯</span>
                Pourquoi quelques infos ?
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Juste le prénom et l'âge pour des réponses parfaitement adaptées à votre enfant !
              </p>
            </div>

            {/* Avantages */}
            <div className="bg-gradient-to-r from-teal-50/80 to-cyan-50/80 rounded-2xl p-4 mb-5">
              <h3 className="font-bold text-teal-800 mb-2 text-sm">
                ✨ Ce que ça permet :
              </h3>
              <ul className="text-gray-600 text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-teal-500 mt-0.5">•</span>
                  <span>Réponses adaptées à son âge avec son prénom</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-teal-500 mt-0.5">•</span>
                  <span>Historique personnalisé de ses découvertes</span>
                </li>
              </ul>
            </div>

            {/* Confidentialité */}
            <div className="text-center mb-5">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-50/80 text-green-700 rounded-full text-sm font-medium">
                🔒 Données 100% confidentielles
              </span>
            </div>

            {/* Bouton principal */}
            <button
              onClick={() => setShowAddChild(true)}
              className="w-full py-4 rounded-2xl font-bold text-white shadow-lg 
                bg-gradient-to-r from-blue-500 to-teal-500 
                hover:from-blue-600 hover:to-teal-600 
                transform hover:scale-[1.02] transition-all duration-200"
            >
              🌟 C'est parti !
            </button>
          </div>

          {/* Option test */}
          <div className="text-center">
            <button
              onClick={() => setShowTestOptions(!showTestOptions)}
              className="text-sm font-medium text-gray-600 hover:text-teal-700 transition-colors underline-offset-2 hover:underline"
            >
              🧪 Juste tester avec Lucas ou Emma ?
            </button>

            {/* Options de test expandable */}
            {showTestOptions && (
              <div className="mt-4 bg-white/70 backdrop-blur-md rounded-2xl p-5 shadow-lg border border-white/50 text-left">
                <p className="font-bold text-gray-800 text-center text-sm mb-4">
                  Mode Test - Choisis ton héros !
                </p>
                
                {/* Sélection personnage */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <label className="cursor-pointer">
                    <input
                      type="radio"
                      value="boy"
                      checked={testGender === 'boy'}
                      onChange={(e) => setTestGender(e.target.value)}
                      className="sr-only"
                    />
                    <div className={`
                      flex flex-col items-center p-4 rounded-xl border-2 transition-all duration-200
                      ${testGender === 'boy' 
                        ? 'border-blue-400 bg-blue-50/80 shadow-md scale-105' 
                        : 'border-gray-200 bg-white/60 hover:border-gray-300'
                      }
                    `}>
                      <div className="text-3xl mb-1">🧑‍🚀</div>
                      <span className={`text-sm font-medium ${testGender === 'boy' ? 'text-blue-600' : 'text-gray-600'}`}>
                        Lucas
                      </span>
                    </div>
                  </label>
                  
                  <label className="cursor-pointer">
                    <input
                      type="radio"
                      value="girl"
                      checked={testGender === 'girl'}
                      onChange={(e) => setTestGender(e.target.value)}
                      className="sr-only"
                    />
                    <div className={`
                      flex flex-col items-center p-4 rounded-xl border-2 transition-all duration-200
                      ${testGender === 'girl' 
                        ? 'border-cyan-400 bg-cyan-50/80 shadow-md scale-105' 
                        : 'border-gray-200 bg-white/60 hover:border-gray-300'
                      }
                    `}>
                      <div className="text-3xl mb-1">👸</div>
                      <span className={`text-sm font-medium ${testGender === 'girl' ? 'text-cyan-600' : 'text-gray-600'}`}>
                        Emma
                      </span>
                    </div>
                  </label>
                </div>

                {/* Sélection âge */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
                    Quel âge ?
                  </label>
                  <select
                    value={testAge}
                    onChange={(e) => setTestAge(parseInt(e.target.value))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-center 
                      focus:border-teal-400 focus:outline-none bg-white/80 appearance-none
                      transition-colors"
                  >
                    {Array.from({ length: 15 }, (_, i) => i + 2).map(age => (
                      <option key={age} value={age}>{age} ans</option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={handleCreateTestChild}
                  disabled={isCreatingTest}
                  className="w-full py-3 rounded-xl font-medium text-white shadow-md transition-all
                    bg-gradient-to-r from-orange-400 to-amber-400 
                    hover:from-orange-500 hover:to-amber-500 
                    disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingTest ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Création...
                    </span>
                  ) : (
                    `🚀 En route avec ${testGender === 'boy' ? 'Lucas' : 'Emma'} !`
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center mt-3">
                  Vous pourrez créer le vrai profil plus tard
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ReassuringChildPrompt;
